import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-1 focus:ring-ring/50 focus:ring-offset-0",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-destructive/25 bg-destructive/10 text-destructive-text hover:bg-destructive/20",
        outline: "text-foreground",

        // Status variants, in the soft-tint style the admin portal already
        // uses by hand (`bg-success/10 text-success-text`, see
        // ClientVisibilityTab). Their absence was the root cause of most
        // palette drift: with no variant to reach for, every status badge
        // hand-rolled raw palette utilities (`bg-amber-500/15 text-amber-700`),
        // which is how ~34 local colour maps and 144 hardcoded colours in
        // lib/statusColors.ts came to exist. Tinted rather than solid so a
        // status never competes with a primary action for attention — and so
        // `accepted` does not render as a brand-navy fill.
        success:
          "border-success/25 bg-success/10 text-success-text hover:bg-success/20",
        warning:
          "border-warning/25 bg-warning/10 text-warning-text hover:bg-warning/20",
        info:
          "border-info/25 bg-info/10 text-info-text hover:bg-info/20",
        muted:
          "border-border bg-muted text-muted-foreground hover:bg-muted/80",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <div ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />
    )
  }
)
Badge.displayName = "Badge"

export { Badge, badgeVariants }
